import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


const LabOrderPanel = ({ commonPanels, onOrderSubmit }) => {
  const [selectedTests, setSelectedTests] = useState([]);
  const [urgency, setUrgency] = useState('routine');

  const handleTestToggle = (testId) => {
    setSelectedTests((prev) =>
      prev?.includes(testId) ? prev?.filter((id) => id !== testId) : [...prev, testId]
    );
  };

  const handlePanelSelect = (panel) => {
    const panelTestIds = panel?.tests?.map((test) => test?.id);
    setSelectedTests((prev) => {
      const allSelected = panelTestIds?.every((id) => prev?.includes(id));
      if (allSelected) {
        return prev?.filter((id) => !panelTestIds?.includes(id));
      } else {
        return [...new Set([...prev, ...panelTestIds])];
      }
    });
  };

  const handleSubmit = () => {
    if (selectedTests?.length > 0) {
      onOrderSubmit({ tests: selectedTests, urgency });
      setSelectedTests([]);
      setUrgency('routine');
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-6 space-y-4 md:space-y-6">
      <div>
        <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">Order Lab Tests</h3>
        <p className="text-sm md:text-base text-muted-foreground">
          Select individual tests or common panels
        </p>
      </div>
      <div className="space-y-4">
        <div>
          <h4 className="text-sm md:text-base font-semibold text-foreground mb-3">Common Panels</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {commonPanels?.map((panel) => (
              <button
                key={panel?.id}
                onClick={() => handlePanelSelect(panel)}
                className="flex items-start gap-3 p-3 md:p-4 rounded-lg border border-border hover:bg-muted transition-smooth text-left"
              >
                <Icon name="FlaskConical" size={20} color="var(--color-primary)" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm md:text-base font-medium text-foreground">{panel?.name}</p>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">
                    {panel?.tests?.length} tests • ${panel?.estimatedCost}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm md:text-base font-semibold text-foreground mb-3">Selected Tests</h4>
          {selectedTests?.length === 0 ? (
            <p className="text-sm md:text-base text-muted-foreground">No tests selected</p>
          ) : (
            <div className="space-y-2">
              {selectedTests?.map((testId) => {
                const test = commonPanels?.flatMap((p) => p?.tests)?.find((t) => t?.id === testId);
                return (
                  <div
                    key={testId}
                    className="flex items-center justify-between p-2 md:p-3 bg-muted rounded-lg"
                  >
                    <span className="text-sm md:text-base text-foreground">{test?.name}</span>
                    <button
                      onClick={() => handleTestToggle(testId)}
                      className="text-destructive hover:text-destructive/80"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <h4 className="text-sm md:text-base font-semibold text-foreground mb-3">Urgency</h4>
          <div className="flex flex-col sm:flex-row gap-2 md:gap-3">
            {['routine', 'urgent', 'stat']?.map((level) => (
              <button
                key={level}
                onClick={() => setUrgency(level)}
                className={`flex-1 px-4 py-2 md:py-3 rounded-lg text-sm md:text-base font-medium transition-smooth ${
                  urgency === level
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground hover:bg-muted/80'
                }`}
              >
                {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <Button
          variant="default"
          fullWidth
          disabled={selectedTests?.length === 0}
          onClick={handleSubmit}
          iconName="Send"
          iconPosition="left"
        >
          Submit Lab Order ({selectedTests?.length} tests)
        </Button>
      </div>
    </div>
  );
};

export default LabOrderPanel;